import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { 
  Return, 
  ReturnFilters, 
  CreateReturnRequest, 
  UpdateReturnRequest, 
  ReturnPolicy 
} from '../models/return.models';

@Injectable({
  providedIn: 'root'
})
export class ReturnsService {

  constructor(private http: HttpClient) { }

  /**
   * Obtiene lista de devoluciones con filtros
   */
  getReturns(filters?: ReturnFilters): Observable<any> {
    let params = new HttpParams();
    
    if (filters) {
      if (filters.page) {
        params = params.append('page', filters.page.toString());
      }
      if (filters.limit) {
        params = params.append('limit', filters.limit.toString());
      }
      if (filters.status) {
        params = params.append('status', filters.status);
      }
      if (filters.clientId) {
        params = params.append('clientId', filters.clientId.toString());
      }
      if (filters.dateFrom) {
        params = params.append('dateFrom', filters.dateFrom);
      }
      if (filters.dateTo) {
        params = params.append('dateTo', filters.dateTo);
      }
      if (filters.returnNumber) {
        params = params.append('returnNumber', filters.returnNumber);
      }
      if (filters.originalVoucherNumber) {
        params = params.append('originalVoucherNumber', filters.originalVoucherNumber);
      }
    }

    return this.http.get(`${environment.apiUrl}returns`, { params });
  }

  /**
   * Obtiene una devolución específica por ID
   */
  getReturn(id: number): Observable<Return> {
    return this.http.get<Return>(`${environment.apiUrl}returns/${id}`);
  }

  /**
   * Crea una nueva devolución
   */
  createReturn(returnData: CreateReturnRequest): Observable<Return> {
    return this.http.post<Return>(`${environment.apiUrl}returns`, returnData);
  }

  /**
   * Actualiza una devolución existente
   */
  updateReturn(id: number, returnData: UpdateReturnRequest): Observable<Return> {
    return this.http.patch<Return>(`${environment.apiUrl}returns/${id}`, returnData);
  }

  /**
   * Elimina una devolución (solo si está en estado PENDING)
   */
  deleteReturn(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}returns/${id}`);
  }

  /**
   * Aprueba una devolución
   */
  approveReturn(id: number, internalNotes?: string): Observable<Return> {
    const body = internalNotes ? { internalNotes } : {};
    return this.http.patch<Return>(`${environment.apiUrl}returns/${id}/approve`, body);
  }

  /**
   * Rechaza una devolución
   */
  rejectReturn(id: number, internalNotes?: string): Observable<Return> {
    const body = internalNotes ? { internalNotes } : {};
    return this.http.patch<Return>(`${environment.apiUrl}returns/${id}/reject`, body);
  }

  /**
   * Procesa una devolución
   */
  processReturn(id: number, internalNotes?: string): Observable<Return> {
    const body = internalNotes ? { internalNotes } : {};
    return this.http.patch<Return>(`${environment.apiUrl}returns/${id}/process`, body);
  }

  /**
   * Obtiene la política de devoluciones
   */
  getReturnPolicy(): Observable<ReturnPolicy> {
    return this.http.get<ReturnPolicy>(`${environment.apiUrl}returns/policy`);
  }

  /**
   * Obtiene comprobantes retornables para un cliente
   */
  getReturnableVouchers(clientId: number): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}vouchers/returnable/${clientId}`);
  }

  /**
   * Valida si un comprobante es retornable
   */
  validateReturnableVoucher(voucherId: number): Observable<any> {
    return this.http.get(`${environment.apiUrl}vouchers/${voucherId}/returnable-validation`);
  }
}